import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tabs,
  Tab,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Add, CheckCircle, Visibility } from '@mui/icons-material';

import { useSiteContext } from '../../hooks/useSiteContext';
import { useAuth } from '../../hooks/useAuth';
import safetyService from '../../services/safetyService';
import {
  SafetyInduction,
  SafetyInductionCompletion,
  SafetyCompetency,
  UserSafetyCompetency,
  SiteSafetyRequirement,
} from '../../types/safety';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface UserSummary {
  id: number;
  username: string;
  full_name: string;
}

const SafetyDashboard: React.FC = () => {
  const { currentSite, isInSiteContext } = useSiteContext();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inductions, setInductions] = useState<SafetyInduction[]>([]);
  const [competencies, setCompetencies] = useState<SafetyCompetency[]>([]);
  const [userCompetencies, setUserCompetencies] = useState<UserSafetyCompetency[]>([]);
  const [siteRequirements, setSiteRequirements] = useState<SiteSafetyRequirement[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);

  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inductionDialogMode, setInductionDialogMode] = useState<'create' | 'edit'>('create');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [completionsDialogOpen, setCompletionsDialogOpen] = useState(false);
  const [selectedInduction, setSelectedInduction] = useState<SafetyInduction | null>(null);
  const [completions, setCompletions] = useState<SafetyInductionCompletion[]>([]);
  const [completionsLoading, setCompletionsLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [competencyDialogOpen, setCompetencyDialogOpen] = useState(false);
  const [competencyDialogMode, setCompetencyDialogMode] = useState<'create' | 'edit'>('create');
  const [userCompetencyDialogOpen, setUserCompetencyDialogOpen] = useState(false);
  const [userCompetencyDialogMode, setUserCompetencyDialogMode] = useState<'create' | 'edit'>('create');
  const [siteRequirementDialogOpen, setSiteRequirementDialogOpen] = useState(false);
  const [siteRequirementDialogMode, setSiteRequirementDialogMode] = useState<'create' | 'edit'>('create');

  const [editingCompetency, setEditingCompetency] = useState<SafetyCompetency | null>(null);
  const [editingUserCompetency, setEditingUserCompetency] = useState<UserSafetyCompetency | null>(null);
  const [editingSiteRequirement, setEditingSiteRequirement] = useState<SiteSafetyRequirement | null>(null);

  const [newInduction, setNewInduction] = useState({
    title: '',
    description: '',
    content: '',
    version: '1.0',
    is_active: true,
    requires_acknowledgment: true,
  });
  const [newCompetency, setNewCompetency] = useState({
    name: '',
    code: '',
    description: '',
    validity_days: '',
    is_active: true,
  });
  const [newUserCompetency, setNewUserCompetency] = useState({
    user: '',
    competency: '',
    achieved_at: '',
    expires_at: '',
    notes: '',
  });
  const [newSiteRequirement, setNewSiteRequirement] = useState({
    applies_to_role: 'recipient',
    requirement_type: 'induction',
    induction_id: '',
    competency_id: '',
    is_active: true,
  });

  const [inductionSearch, setInductionSearch] = useState('');
  const [inductionStatusFilter, setInductionStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [competencySearch, setCompetencySearch] = useState('');
  const [competencyStatusFilter, setCompetencyStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [userCompetencySearch, setUserCompetencySearch] = useState('');
  const [userCompetencyExpiryFilter, setUserCompetencyExpiryFilter] = useState<'all' | 'expired' | 'expiring' | 'valid' | 'no_expiry'>('all');
  const [siteRequirementSearch, setSiteRequirementSearch] = useState('');
  const [siteRequirementRoleFilter, setSiteRequirementRoleFilter] = useState<'all' | 'issuer' | 'recipient' | 'supervisor' | 'worker'>('all');
  const [siteRequirementStatusFilter, setSiteRequirementStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const siteId = currentSite.siteId;
  const canCreateInduction = useMemo(() => !!user?.can_manage_site_data, [user]);

  const fetchInductions = useCallback(async () => {
    if (!siteId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await safetyService.inductions.list({ site: siteId });
      setInductions(data);
    } catch (err) {
      console.error('Failed to load inductions:', err);
      setError('Failed to load site inductions');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const fetchCompetencies = useCallback(async () => {
    try {
      const data = await safetyService.competencies.list({ is_active: true });
      setCompetencies(data);
    } catch (err) {
      console.error('Failed to load competencies:', err);
      setError('Failed to load competencies');
    }
  }, []);

  const fetchUserCompetencies = useCallback(async () => {
    try {
      const data = await safetyService.userCompetencies.list();
      setUserCompetencies(data);
    } catch (err) {
      console.error('Failed to load user competencies:', err);
      setError('Failed to load user competencies');
    }
  }, []);

  const fetchSiteRequirements = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await safetyService.siteRequirements.list({ site: siteId });
      setSiteRequirements(data);
    } catch (err) {
      console.error('Failed to load site requirements:', err);
      setError('Failed to load site requirements');
    }
  }, [siteId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/?page_size=200`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const results = data.results || data;
      setUsers(
        results.map((item: any) => ({
          id: item.id,
          username: item.username,
          full_name: item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim(),
        }))
      );
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    }
  }, []);

  useEffect(() => {
    if (siteId) {
      fetchInductions();
      fetchSiteRequirements();
    }
    fetchCompetencies();
    fetchUserCompetencies();
    fetchUsers();
  }, [siteId, fetchInductions, fetchSiteRequirements, fetchCompetencies, fetchUserCompetencies, fetchUsers]);

  const handleOpenCreate = () => {
    setInductionDialogMode('create');
    setSelectedInduction(null);
    setCreateDialogOpen(true);
  };

  const handleCreateInduction = async () => {
    if (!siteId) return;
    try {
      setLoading(true);
      if (inductionDialogMode === 'edit' && selectedInduction) {
        await safetyService.inductions.update(selectedInduction.id, {
          title: newInduction.title,
          description: newInduction.description,
          content: newInduction.content,
          version: newInduction.version,
          is_active: newInduction.is_active,
          requires_acknowledgment: newInduction.requires_acknowledgment,
        });
      } else {
        await safetyService.inductions.create({
          site: siteId,
          title: newInduction.title,
          description: newInduction.description,
          content: newInduction.content,
          version: newInduction.version,
          is_active: newInduction.is_active,
          requires_acknowledgment: newInduction.requires_acknowledgment,
        });
      }
      setCreateDialogOpen(false);
      setNewInduction({
        title: '',
        description: '',
        content: '',
        version: '1.0',
        is_active: true,
        requires_acknowledgment: true,
      });
      setSelectedInduction(null);
      fetchInductions();
    } catch (err) {
      console.error('Failed to save induction:', err);
      setError('Failed to save induction');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInduction = (induction: SafetyInduction) => {
    setSelectedInduction(induction);
    setInductionDialogMode('edit');
    setNewInduction({
      title: induction.title,
      description: induction.description || '',
      content: induction.content,
      version: induction.version,
      is_active: induction.is_active,
      requires_acknowledgment: induction.requires_acknowledgment,
    });
    setCreateDialogOpen(true);
  };

  const handleDeactivateInduction = async (induction: SafetyInduction) => {
    try {
      setLoading(true);
      await safetyService.inductions.update(induction.id, { is_active: false });
      fetchInductions();
    } catch (err) {
      console.error('Failed to deactivate induction:', err);
      setError('Failed to deactivate induction');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInduction = (induction: SafetyInduction) => {
    setSelectedInduction(induction);
    setAcknowledged(false);
    setViewDialogOpen(true);
  };

  const handleCompleteInduction = async () => {
    if (!selectedInduction) return;
    try {
      setLoading(true);
      await safetyService.inductions.complete(selectedInduction.id, acknowledged);
      setViewDialogOpen(false);
      setSelectedInduction(null);
      fetchInductions();
    } catch (err) {
      console.error('Failed to complete induction:', err);
      setError('Failed to complete induction');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompletions = async (induction: SafetyInduction) => {
    try {
      setCompletionsLoading(true);
      setSelectedInduction(induction);
      const data = await safetyService.inductions.completions(induction.id);
      setCompletions(data);
      setCompletionsDialogOpen(true);
    } catch (err) {
      console.error('Failed to load completions:', err);
      setError('Failed to load induction completions');
    } finally {
      setCompletionsLoading(false);
    }
  };

  const handleCreateCompetency = async () => {
    try {
      setLoading(true);
      if (competencyDialogMode === 'edit' && editingCompetency) {
        await safetyService.competencies.update(editingCompetency.id, {
          name: newCompetency.name,
          code: newCompetency.code,
          description: newCompetency.description,
          validity_days: newCompetency.validity_days ? Number(newCompetency.validity_days) : null,
          is_active: newCompetency.is_active,
        });
      } else {
        await safetyService.competencies.create({
          name: newCompetency.name,
          code: newCompetency.code,
          description: newCompetency.description,
          validity_days: newCompetency.validity_days ? Number(newCompetency.validity_days) : null,
          is_active: newCompetency.is_active,
        });
      }
      setCompetencyDialogOpen(false);
      setNewCompetency({
        name: '',
        code: '',
        description: '',
        validity_days: '',
        is_active: true,
      });
      setEditingCompetency(null);
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to save competency:', err);
      setError('Failed to save competency');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompetency = (competency: SafetyCompetency) => {
    setCompetencyDialogMode('edit');
    setEditingCompetency(competency);
    setNewCompetency({
      name: competency.name,
      code: competency.code,
      description: competency.description || '',
      validity_days: competency.validity_days?.toString() || '',
      is_active: competency.is_active,
    });
    setCompetencyDialogOpen(true);
  };

  const handleDeactivateCompetency = async (competency: SafetyCompetency) => {
    try {
      setLoading(true);
      await safetyService.competencies.update(competency.id, { is_active: false });
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to deactivate competency:', err);
      setError('Failed to deactivate competency');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCompetency = async () => {
    try {
      setLoading(true);
      if (userCompetencyDialogMode === 'edit' && editingUserCompetency) {
        await safetyService.userCompetencies.update(editingUserCompetency.id, {
          achieved_at: newUserCompetency.achieved_at,
          expires_at: newUserCompetency.expires_at || null,
          notes: newUserCompetency.notes,
        });
      } else {
        await safetyService.userCompetencies.create({
          user: Number(newUserCompetency.user),
          competency: Number(newUserCompetency.competency),
          achieved_at: newUserCompetency.achieved_at,
          expires_at: newUserCompetency.expires_at || null,
          notes: newUserCompetency.notes,
        });
      }
      setUserCompetencyDialogOpen(false);
      setNewUserCompetency({
        user: '',
        competency: '',
        achieved_at: '',
        expires_at: '',
        notes: '',
      });
      setEditingUserCompetency(null);
      fetchUserCompetencies();
    } catch (err) {
      console.error('Failed to save user competency:', err);
      setError('Failed to save user competency');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUserCompetency = (record: UserSafetyCompetency) => {
    setUserCompetencyDialogMode('edit');
    setEditingUserCompetency(record);
    setNewUserCompetency({
      user: String(record.user),
      competency: String(record.competency),
      achieved_at: record.achieved_at,
      expires_at: record.expires_at || '',
      notes: record.notes || '',
    });
    setUserCompetencyDialogOpen(true);
  };

  const handleCreateSiteRequirement = async () => {
    if (!siteId) return;
    try {
      setLoading(true);
      if (siteRequirementDialogMode === 'edit' && editingSiteRequirement) {
        await safetyService.siteRequirements.update(editingSiteRequirement.id, {
          site: siteId,
          applies_to_role: newSiteRequirement.applies_to_role as 'issuer' | 'recipient' | 'supervisor' | 'worker',
          induction: newSiteRequirement.requirement_type === 'induction' ? Number(newSiteRequirement.induction_id) : null,
          competency: newSiteRequirement.requirement_type === 'competency' ? Number(newSiteRequirement.competency_id) : null,
          is_active: newSiteRequirement.is_active,
        });
      } else {
        await safetyService.siteRequirements.create({
          site: siteId,
          applies_to_role: newSiteRequirement.applies_to_role as 'issuer' | 'recipient' | 'supervisor' | 'worker',
          induction: newSiteRequirement.requirement_type === 'induction' ? Number(newSiteRequirement.induction_id) : null,
          competency: newSiteRequirement.requirement_type === 'competency' ? Number(newSiteRequirement.competency_id) : null,
          is_active: newSiteRequirement.is_active,
        });
      }
      setSiteRequirementDialogOpen(false);
      setNewSiteRequirement({
        applies_to_role: 'recipient',
        requirement_type: 'induction',
        induction_id: '',
        competency_id: '',
        is_active: true,
      });
      setEditingSiteRequirement(null);
      fetchSiteRequirements();
    } catch (err) {
      console.error('Failed to save site requirement:', err);
      setError('Failed to save site requirement');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSiteRequirement = (requirement: SiteSafetyRequirement) => {
    setSiteRequirementDialogMode('edit');
    setEditingSiteRequirement(requirement);
    setNewSiteRequirement({
      applies_to_role: requirement.applies_to_role,
      requirement_type: requirement.induction ? 'induction' : 'competency',
      induction_id: requirement.induction ? String(requirement.induction) : '',
      competency_id: requirement.competency ? String(requirement.competency) : '',
      is_active: requirement.is_active,
    });
    setSiteRequirementDialogOpen(true);
  };

  const handleDeactivateSiteRequirement = async (requirement: SiteSafetyRequirement) => {
    try {
      setLoading(true);
      await safetyService.siteRequirements.update(requirement.id, { is_active: false });
      fetchSiteRequirements();
    } catch (err) {
      console.error('Failed to deactivate site requirement:', err);
      setError('Failed to deactivate site requirement');
    } finally {
      setLoading(false);
    }
  };

  const getExpiryChip = (expiresAt: string | null) => {
    if (!expiresAt) {
      return <Chip size="small" label="No expiry" />;
    }
    const today = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return <Chip size="small" label="Expired" color="error" />;
    }
    if (diffDays <= 30) {
      return <Chip size="small" label="Expiring" color="warning" />;
    }
    return <Chip size="small" label="Valid" color="success" />;
  };

  const matchesText = (value: string, search: string) =>
    value.toLowerCase().includes(search.trim().toLowerCase());

  const getExpiryStatus = (expiresAt: string | null) => {
    if (!expiresAt) return 'no_expiry';
    const today = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring';
    return 'valid';
  };

  const filteredInductions = inductions
    .filter((item) => {
      if (inductionStatusFilter === 'active' && !item.is_active) return false;
      if (inductionStatusFilter === 'inactive' && item.is_active) return false;
      if (!inductionSearch.trim()) return true;
      return (
        matchesText(item.title, inductionSearch) ||
        matchesText(item.description || '', inductionSearch) ||
        matchesText(item.content || '', inductionSearch) ||
        matchesText(item.version || '', inductionSearch)
      );
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  const filteredCompetencies = competencies
    .filter((item) => {
      if (competencyStatusFilter === 'active' && !item.is_active) return false;
      if (competencyStatusFilter === 'inactive' && item.is_active) return false;
      if (!competencySearch.trim()) return true;
      return (
        matchesText(item.name, competencySearch) ||
        matchesText(item.code, competencySearch) ||
        matchesText(item.description || '', competencySearch)
      );
    })
    .sort((a, b) => a.code.localeCompare(b.code));

  const filteredUserCompetencies = userCompetencies
    .filter((record) => {
      if (userCompetencyExpiryFilter !== 'all' && getExpiryStatus(record.expires_at) !== userCompetencyExpiryFilter) {
        return false;
      }
      if (!userCompetencySearch.trim()) return true;
      const userName = record.user_detail?.full_name || record.user_detail?.username || '';
      const competencyText = record.competency_detail?.name || record.competency_detail?.code || '';
      return matchesText(userName, userCompetencySearch) || matchesText(competencyText, userCompetencySearch);
    })
    .sort((a, b) => {
      const nameA = a.user_detail?.full_name || a.user_detail?.username || '';
      const nameB = b.user_detail?.full_name || b.user_detail?.username || '';
      return nameA.localeCompare(nameB);
    });

  const filteredSiteRequirements = siteRequirements
    .filter((item) => {
      if (siteRequirementStatusFilter === 'active' && !item.is_active) return false;
      if (siteRequirementStatusFilter === 'inactive' && item.is_active) return false;
      if (siteRequirementRoleFilter !== 'all' && item.applies_to_role !== siteRequirementRoleFilter) return false;
      if (!siteRequirementSearch.trim()) return true;
      const requirementText = item.induction_title || item.competency_code || '';
      return (
        matchesText(item.applies_to_role, siteRequirementSearch) ||
        matchesText(requirementText, siteRequirementSearch)
      );
    })
    .sort((a, b) => a.applies_to_role.localeCompare(b.applies_to_role));

  if (!isInSiteContext || !siteId) {
    return (
      <Box p={3}>
        <Alert severity="info">Select a site to manage safety inductions.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Safety Dashboard</Typography>
        {tabValue === 0 && canCreateInduction && (
          <Button startIcon={<Add />} variant="contained" onClick={handleOpenCreate}>
            New Induction
          </Button>
        )}
        {tabValue === 1 && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => {
              setCompetencyDialogMode('create');
              setEditingCompetency(null);
              setNewCompetency({
                name: '',
                code: '',
                description: '',
                validity_days: '',
                is_active: true,
              });
              setCompetencyDialogOpen(true);
            }}
          >
            New Competency
          </Button>
        )}
        {tabValue === 2 && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => {
              setUserCompetencyDialogMode('create');
              setEditingUserCompetency(null);
              setNewUserCompetency({
                user: '',
                competency: '',
                achieved_at: '',
                expires_at: '',
                notes: '',
              });
              setUserCompetencyDialogOpen(true);
            }}
          >
            Assign Competency
          </Button>
        )}
        {tabValue === 3 && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => {
              setSiteRequirementDialogMode('create');
              setEditingSiteRequirement(null);
              setNewSiteRequirement({
                applies_to_role: 'recipient',
                requirement_type: 'induction',
                induction_id: '',
                competency_id: '',
                is_active: true,
              });
              setSiteRequirementDialogOpen(true);
            }}
          >
            New Requirement
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 2 }}>
            <Tab label="Inductions" />
            <Tab label="Competencies" />
            <Tab label="User Competencies" />
            <Tab label="Site Requirements" />
          </Tabs>

          {tabValue === 0 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Site Inductions</Typography>
                {loading && <CircularProgress size={20} />}
              </Stack>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="Search"
                  value={inductionSearch}
                  onChange={(e) => setInductionSearch(e.target.value)}
                  fullWidth
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={inductionStatusFilter}
                    onChange={(e) => setInductionStatusFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Completion</TableCell>
                    <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInductions.map((induction) => (
                      <TableRow key={induction.id}>
                        <TableCell>
                          <Typography fontWeight={600}>{induction.title}</Typography>
                          {induction.description && (
                            <Typography variant="body2" color="text.secondary">
                              {induction.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{induction.version}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={induction.is_active ? 'Active' : 'Inactive'}
                            color={induction.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {induction.current_user_completed ? (
                            <Chip
                              size="small"
                              icon={<CheckCircle />}
                              label="Completed"
                              color="success"
                            />
                          ) : (
                            <Chip size="small" label="Pending" color="warning" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleOpenInduction(induction)}
                            >
                              View
                            </Button>
                            {canCreateInduction && (
                              <Button size="small" onClick={() => handleEditInduction(induction)}>
                                Edit
                              </Button>
                            )}
                            {canCreateInduction && induction.is_active && (
                              <Button size="small" onClick={() => handleDeactivateInduction(induction)}>
                                Deactivate
                              </Button>
                            )}
                            <Button
                              size="small"
                              onClick={() => handleViewCompletions(induction)}
                            >
                              Completions
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredInductions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography color="text.secondary">No inductions found for this site.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tabValue === 1 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Competencies</Typography>
                {loading && <CircularProgress size={20} />}
              </Stack>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="Search"
                  value={competencySearch}
                  onChange={(e) => setCompetencySearch(e.target.value)}
                  fullWidth
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={competencyStatusFilter}
                    onChange={(e) => setCompetencyStatusFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Validity (days)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompetencies.map((competency) => (
                      <TableRow key={competency.id}>
                        <TableCell>{competency.code}</TableCell>
                        <TableCell>{competency.name}</TableCell>
                        <TableCell>{competency.validity_days ?? 'No expiry'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={competency.is_active ? 'Active' : 'Inactive'}
                            color={competency.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => handleEditCompetency(competency)}>
                              Edit
                            </Button>
                            {competency.is_active && (
                              <Button size="small" onClick={() => handleDeactivateCompetency(competency)}>
                                Deactivate
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredCompetencies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography color="text.secondary">No competencies found.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tabValue === 2 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">User Competencies</Typography>
                {loading && <CircularProgress size={20} />}
              </Stack>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="Search"
                  value={userCompetencySearch}
                  onChange={(e) => setUserCompetencySearch(e.target.value)}
                  fullWidth
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Expiry</InputLabel>
                  <Select
                    label="Expiry"
                    value={userCompetencyExpiryFilter}
                    onChange={(e) => setUserCompetencyExpiryFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="expiring">Expiring</MenuItem>
                    <MenuItem value="valid">Valid</MenuItem>
                    <MenuItem value="no_expiry">No expiry</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Competency</TableCell>
                      <TableCell>Achieved</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUserCompetencies.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          {record.user_detail?.full_name || record.user_detail?.username || `User ${record.user}`}
                        </TableCell>
                        <TableCell>{record.competency_detail?.name || record.competency_detail?.code}</TableCell>
                        <TableCell>{record.achieved_at}</TableCell>
                        <TableCell>{record.expires_at || 'No expiry'}</TableCell>
                        <TableCell>{getExpiryChip(record.expires_at)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleEditUserCompetency(record)}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUserCompetencies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography color="text.secondary">No user competencies found.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tabValue === 3 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Site Requirements</Typography>
                {loading && <CircularProgress size={20} />}
              </Stack>
              <Stack direction="row" spacing={2} mb={2}>
                <TextField
                  label="Search"
                  value={siteRequirementSearch}
                  onChange={(e) => setSiteRequirementSearch(e.target.value)}
                  fullWidth
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={siteRequirementRoleFilter}
                    onChange={(e) => setSiteRequirementRoleFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="issuer">Issuer</MenuItem>
                    <MenuItem value="recipient">Recipient</MenuItem>
                    <MenuItem value="supervisor">Supervisor</MenuItem>
                    <MenuItem value="worker">Worker</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={siteRequirementStatusFilter}
                    onChange={(e) => setSiteRequirementStatusFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Requirement</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSiteRequirements.map((requirement) => (
                      <TableRow key={requirement.id}>
                        <TableCell>{requirement.applies_to_role}</TableCell>
                        <TableCell>
                          {requirement.induction_title
                            ? `Induction: ${requirement.induction_title}`
                            : `Competency: ${requirement.competency_code}`}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={requirement.is_active ? 'Active' : 'Inactive'}
                            color={requirement.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => handleEditSiteRequirement(requirement)}>
                              Edit
                            </Button>
                            {requirement.is_active && (
                              <Button size="small" onClick={() => handleDeactivateSiteRequirement(requirement)}>
                                Deactivate
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSiteRequirements.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography color="text.secondary">No requirements configured for this site.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{inductionDialogMode === 'edit' ? 'Edit Induction' : 'Create New Induction'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Title"
              value={newInduction.title}
              onChange={(e) => setNewInduction({ ...newInduction, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newInduction.description}
              onChange={(e) => setNewInduction({ ...newInduction, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Content"
              value={newInduction.content}
              onChange={(e) => setNewInduction({ ...newInduction, content: e.target.value })}
              fullWidth
              multiline
              minRows={6}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Version"
                value={newInduction.version}
                onChange={(e) => setNewInduction({ ...newInduction, version: e.target.value })}
                sx={{ maxWidth: 160 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newInduction.is_active}
                    onChange={(e) => setNewInduction({ ...newInduction, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newInduction.requires_acknowledgment}
                    onChange={(e) => setNewInduction({ ...newInduction, requires_acknowledgment: e.target.checked })}
                  />
                }
                label="Requires Acknowledgment"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateInduction}
            disabled={!newInduction.title || !newInduction.content}
          >
            {inductionDialogMode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={competencyDialogOpen} onClose={() => setCompetencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{competencyDialogMode === 'edit' ? 'Edit Competency' : 'Create Competency'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Name"
              value={newCompetency.name}
              onChange={(e) => setNewCompetency({ ...newCompetency, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Code"
              value={newCompetency.code}
              onChange={(e) => setNewCompetency({ ...newCompetency, code: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newCompetency.description}
              onChange={(e) => setNewCompetency({ ...newCompetency, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Validity Days (optional)"
              value={newCompetency.validity_days}
              onChange={(e) => setNewCompetency({ ...newCompetency, validity_days: e.target.value })}
              type="number"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newCompetency.is_active}
                  onChange={(e) => setNewCompetency({ ...newCompetency, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompetencyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCompetency}
            disabled={!newCompetency.name || !newCompetency.code}
          >
            {competencyDialogMode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userCompetencyDialogOpen} onClose={() => setUserCompetencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{userCompetencyDialogMode === 'edit' ? 'Edit User Competency' : 'Assign Competency to User'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                label="User"
                value={newUserCompetency.user}
                onChange={(e) => setNewUserCompetency({ ...newUserCompetency, user: e.target.value })}
                disabled={userCompetencyDialogMode === 'edit'}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.full_name || u.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Competency</InputLabel>
              <Select
                label="Competency"
                value={newUserCompetency.competency}
                onChange={(e) => setNewUserCompetency({ ...newUserCompetency, competency: e.target.value })}
                disabled={userCompetencyDialogMode === 'edit'}
              >
                {competencies.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.code} - {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Achieved At"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newUserCompetency.achieved_at}
              onChange={(e) => setNewUserCompetency({ ...newUserCompetency, achieved_at: e.target.value })}
            />
            <TextField
              label="Expires At (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newUserCompetency.expires_at}
              onChange={(e) => setNewUserCompetency({ ...newUserCompetency, expires_at: e.target.value })}
            />
            <TextField
              label="Notes"
              value={newUserCompetency.notes}
              onChange={(e) => setNewUserCompetency({ ...newUserCompetency, notes: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserCompetencyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignCompetency}
            disabled={!newUserCompetency.user || !newUserCompetency.competency || !newUserCompetency.achieved_at}
          >
            {userCompetencyDialogMode === 'edit' ? 'Save' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={siteRequirementDialogOpen} onClose={() => setSiteRequirementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{siteRequirementDialogMode === 'edit' ? 'Edit Site Requirement' : 'Create Site Requirement'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Applies To Role</InputLabel>
              <Select
                label="Applies To Role"
                value={newSiteRequirement.applies_to_role}
                onChange={(e) => setNewSiteRequirement({ ...newSiteRequirement, applies_to_role: e.target.value as string })}
              >
                <MenuItem value="issuer">Issuer</MenuItem>
                <MenuItem value="recipient">Recipient</MenuItem>
                <MenuItem value="supervisor">Supervisor</MenuItem>
                <MenuItem value="worker">Worker</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Requirement Type</InputLabel>
              <Select
                label="Requirement Type"
                value={newSiteRequirement.requirement_type}
                onChange={(e) => setNewSiteRequirement({
                  ...newSiteRequirement,
                  requirement_type: e.target.value as string,
                })}
              >
                <MenuItem value="induction">Induction</MenuItem>
                <MenuItem value="competency">Competency</MenuItem>
              </Select>
            </FormControl>
            {newSiteRequirement.requirement_type === 'induction' && (
              <FormControl fullWidth>
                <InputLabel>Induction</InputLabel>
                <Select
                  label="Induction"
                  value={newSiteRequirement.induction_id}
                  onChange={(e) => setNewSiteRequirement({ ...newSiteRequirement, induction_id: e.target.value })}
                >
                  {inductions.map((induction) => (
                    <MenuItem key={induction.id} value={String(induction.id)}>
                      {induction.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {newSiteRequirement.requirement_type === 'competency' && (
              <FormControl fullWidth>
                <InputLabel>Competency</InputLabel>
                <Select
                  label="Competency"
                  value={newSiteRequirement.competency_id}
                  onChange={(e) => setNewSiteRequirement({ ...newSiteRequirement, competency_id: e.target.value })}
                >
                  {competencies.map((competency) => (
                    <MenuItem key={competency.id} value={String(competency.id)}>
                      {competency.code} - {competency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={newSiteRequirement.is_active}
                  onChange={(e) => setNewSiteRequirement({ ...newSiteRequirement, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteRequirementDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSiteRequirement}
            disabled={
              (newSiteRequirement.requirement_type === 'induction' && !newSiteRequirement.induction_id) ||
              (newSiteRequirement.requirement_type === 'competency' && !newSiteRequirement.competency_id)
            }
          >
            {siteRequirementDialogMode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedInduction?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Version {selectedInduction?.version}
            </Typography>
            <Divider />
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {selectedInduction?.content}
            </Typography>
            {selectedInduction?.requires_acknowledgment && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                }
                label="I acknowledge this induction"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {!selectedInduction?.current_user_completed && (
            <Button
              variant="contained"
              onClick={handleCompleteInduction}
              disabled={selectedInduction?.requires_acknowledgment && !acknowledged}
            >
              Mark Completed
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={completionsDialogOpen} onClose={() => setCompletionsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Induction Completions</DialogTitle>
        <DialogContent>
          {completionsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Completed At</TableCell>
                    <TableCell>Acknowledged</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completions.map((completion) => (
                    <TableRow key={completion.id}>
                      <TableCell>
                        {completion.user_detail?.full_name || completion.user_detail?.username || 'Unknown'}
                      </TableCell>
                      <TableCell>{new Date(completion.completed_at).toLocaleString()}</TableCell>
                      <TableCell>{completion.acknowledged ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                  {completions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="text.secondary">No completions yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SafetyDashboard;